/**
 * MAIA widget state, shared by the dashboard tile and its expanded view.
 *
 * Why shared: the dashboard renders the widget twice for the same patient.
 * The tile stays mounted in the grid while the expanded copy is open in a
 * WidgetModal (DashboardGrid, smallWidget true / false). A conversation
 * started in one must continue in the other: same messages, date range,
 * detail level, and any reply still on its way.
 *
 * How:
 * 1. The state lives in one zustand store for the page (`useMaiaChatStore`),
 *    one entry per `agency:patientNumber`. Both copies import this module, so
 *    they read the same store.
 * 2. `getMaiaChat` returns the actions object for that key, creating it on
 *    first use. The second copy gets the same object, not a copy.
 * 3. Every change goes through `set()`, which updates that patient's entry in
 *    the store; zustand re-renders each mounted copy that reads it (and no
 *    other patient's widget).
 * 4. Actions (startChat, sendMessage, cancelPending, done) belong to the
 *    object, not to a component. A pending /message resolves into this state
 *    even if the copy that sent it has closed, so the other copy still shows
 *    the reply.
 *
 * Nothing is copied between the two views and nothing is reloaded from the
 * gateway, so they cannot drift apart or loop. To see what the widget shows
 * and why, put a breakpoint in `set()`.
 *
 * Memory only, on purpose: a page reload empties the store and the widget opens
 * on history. Answers are not lost: the gateway stores each question and
 * answer, and they appear in the history grid.
 */
import create from 'zustand';

import type {
  AgentGateway,
  PatientChatChartStatus,
  PatientChatMessageBody,
  PatientChatResponseDetail,
  PatientChatStartBody,
  PatientConversationSummary,
  TokenStatus,
} from '@mummssoftware/utils';

import {
  DEFAULT_RESPONSE_DETAIL,
  SLOW_REPLY_MS,
  TOKEN_USAGE_NOTICE_PERCENT,
} from '../constants';
import type {
  AnswerFeedback,
  ChatMessage,
  ConversationQuestion,
  ConversationRef,
  MaiaDateRange,
  MaiaView,
} from '../types';
import { defaultDateRange, toApiDate } from '../utils/dateRange';
import {
  errorMessage,
  isGatewayError,
  tokenLimitFromError,
  unwrap,
} from '../utils/gatewayError';
import { tokenLimitText } from '../utils/formatters';
import defaults from '../translations/en.json';

/** See sendMessage. */
export type SendResult = boolean | 'retry';

/** The note above the composer: the share of the token pool used, or the limit. */
export type TokenNotice = { text: string; limitReached: boolean };

/** Everything the widget shows for one patient. */
export type MaiaChatState = {
  view: MaiaView;
  /** The history row open in the past view. */
  pastRow: PatientConversationSummary | null;
  /** Set when one of its questions is open instead of the whole conversation. */
  pastQuestion: ConversationQuestion | null;
  dateRange: MaiaDateRange;
  responseDetail: PatientChatResponseDetail;
  conversation: ConversationRef | null;
  messages: ChatMessage[];
  chartStatus: PatientChatChartStatus | string | null;
  isStarting: boolean;
  isSending: boolean;
  /** The pending reply passed SLOW_REPLY_MS; the view asks whether to cancel. */
  isSlowReply: boolean;
  /** The agent answered 423: it is still on an earlier question. */
  isAgentBusy: boolean;
  isCancelling: boolean;
  error: string | null;
  /**
   * The open chat's token note, from the gateway's last word on the agency's
   * pool (a /start, a reply or a 429); null when there is none or it was closed.
   */
  tokenNotice: TokenNotice | null;
};

const initialState = (): MaiaChatState => ({
  view: 'history',
  pastRow: null,
  pastQuestion: null,
  dateRange: defaultDateRange(),
  responseDetail: DEFAULT_RESPONSE_DETAIL,
  conversation: null,
  messages: [],
  chartStatus: null,
  isStarting: false,
  isSending: false,
  isSlowReply: false,
  isAgentBusy: false,
  isCancelling: false,
  error: null,
  tokenNotice: null,
});

/** The limit message, the % used from TOKEN_USAGE_NOTICE_PERCENT, or null. */
const tokenNoticeFor = (
  status: TokenStatus | null | undefined,
): TokenNotice | null => {
  if (!status) {
    return null;
  }
  if (status.limitReached) {
    return { text: tokenLimitText(status), limitReached: true };
  }
  return status.percentUsed >= TOKEN_USAGE_NOTICE_PERCENT
    ? {
        text: defaults.tokenUsage.replace(
          '{percent}',
          String(status.percentUsed),
        ),
        limitReached: false,
      }
    : null;
};

/** What /start gave: a conversation, the agency's token limit, or an error. */
type StartOutcome =
  | {
      conversation: ConversationRef;
      chartStatus: PatientChatChartStatus | string | null;
      tokenStatus: TokenStatus | null;
    }
  | { limit: TokenStatus }
  | { error: string };

const nextId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** The message for a /message that failed: an HTTP error, or no answer at all. */
const sendErrorText = (
  err: unknown,
  chartStatus: MaiaChatState['chartStatus'],
  waitedMs: number,
): string => {
  if (isGatewayError(err)) {
    switch (err.status) {
      case 404:
        return defaults.conversationExpired;
      case 409:
        return defaults.conversationEnded;
      case 504:
        return defaults.replyTimedOut; // the gateway gave up waiting
      default:
        return defaults.sendMessageError;
    }
  }
  // No HTTP answer: the connection dropped. While the chart prints that is
  // "not ready yet"; after a long wait, "didn't answer in time".
  if (chartStatus === 'generating') {
    return defaults.preparingChartRetry;
  }
  if (waitedMs >= SLOW_REPLY_MS) {
    return defaults.replyTimedOut;
  }
  return errorMessage(err, defaults.sendMessageError);
};

/**
 * Why the agent gave no answer, as a message for the user; null when it
 * answered. The agent dropped the question in each case, so nothing was saved.
 */
const notAnsweredText = (
  body: Partial<PatientChatMessageBody>,
): string | null => {
  if (body.timedOut) {
    return defaults.answerTimedOut;
  }
  if (body.chartNotReady) {
    return defaults.chartNotReady;
  }
  if (body.chartFailed) {
    return defaults.chartFailed;
  }
  if (body.error) {
    return defaults.answerFailed; // a code for the logs, never shown
  }
  return null;
};

type MaiaChatsState = {
  /** Each patient's widget state, by `agency:patientNumber`. */
  chats: Record<string, MaiaChatState>;
};

/**
 * Every patient's MAIA widget state for the page. Module level on purpose:
 * this is what makes the tile and the expanded view use the same state.
 * zustand 3 because the monorepo is on TypeScript 3.9 (zustand 4's types need 4.0+).
 */
export const useMaiaChatStore = create<MaiaChatsState>(() => ({ chats: {} }));

let unkeyedChats = 0;

/**
 * One patient's MAIA actions over its entry in `useMaiaChatStore` (see the top
 * of this file). `key` is `agency:patientNumber`; without one (tests) the chat
 * gets an entry of its own.
 */
export const createMaiaChat = (
  agentGateway: AgentGateway,
  patientNumber: string | null | undefined,
  key = `unkeyed:${(unkeyedChats += 1)}`,
) => {
  // the state until the first change, one object so it reads the same every render
  const initial = initialState();
  const read = (): MaiaChatState =>
    useMaiaChatStore.getState().chats[key] || initial;
  /**
   * The send whose reply we still show. Cancel, Done and reset clear it, so a
   * late reply cannot land in the chat.
   */
  let pendingSendId: number | null = null;
  let sendSeq = 0;
  let slowTimer: number | undefined;

  const set = (patch: Partial<MaiaChatState>) =>
    useMaiaChatStore.setState(({ chats }) => ({
      chats: { ...chats, [key]: { ...(chats[key] || initial), ...patch } },
    }));

  /** Starts the countdown to the "taking longer than usual" notice. */
  const armSlowTimer = (sendId: number) => {
    window.clearTimeout(slowTimer);
    slowTimer = window.setTimeout(() => {
      if (pendingSendId === sendId) {
        set({ isSlowReply: true });
      }
    }, SLOW_REPLY_MS);
  };

  /**
   * A reply's pool status (absent on older gateways) updates the note while it
   * shows, and brings it back at the limit; a closed note stays closed below it.
   */
  const updateTokenNotice = (status: TokenStatus | null | undefined) => {
    if (status && (read().tokenNotice || status.limitReached)) {
      set({ tokenNotice: tokenNoticeFor(status) });
    }
  };

  const releasePending = () => {
    window.clearTimeout(slowTimer);
    pendingSendId = null;
    set({ isSending: false, isSlowReply: false });
  };

  const markCancelled = (messageId: string) =>
    set({
      messages: read().messages.map((m) =>
        m.id === messageId ? { ...m, cancelled: true } : m,
      ),
    });

  /** Asks the gateway to stop the running question. */
  const requestCancel = async (
    externalId: string,
  ): Promise<'stopped' | 'idle' | 'failed'> => {
    try {
      const result = unwrap(
        await agentGateway.cancelPatientChatMessage(externalId),
      );
      return result.body?.cancelled ? 'stopped' : 'idle';
    } catch {
      return 'failed';
    }
  };

  /** /start for the range; never throws. */
  const requestStart = async (
    patient: string,
    range: MaiaDateRange,
  ): Promise<StartOutcome> => {
    try {
      const started = unwrap(
        await agentGateway.startPatientChat({
          patientNumber: patient,
          responseDetail: read().responseDetail,
          fromDate: toApiDate(range.fromDate),
          toDate: toApiDate(range.toDate),
        }),
      );
      const body: Partial<PatientChatStartBody> = started.body || {};
      const externalId = body.conversationId;
      if (!externalId) {
        return { error: defaults.startChatError };
      }
      // The gateway sends it whenever /start succeeds; without it Done
      // cannot call /end and the agent session just expires.
      const recordId = started.conversationRecordId ?? null;
      if (recordId == null) {
        // eslint-disable-next-line no-console
        console.warn(
          `[MAIA] no conversationRecordId for ${externalId}; Done will not call /end`,
        );
      }
      return {
        conversation: { externalId, recordId },
        chartStatus: body.chartStatus ?? null,
        tokenStatus: started.tokenStatus ?? null,
      };
    } catch (err) {
      // at the limit the gateway prints no chart and says when to come back
      const limit = await tokenLimitFromError(err);
      return limit
        ? { limit }
        : { error: errorMessage(err, defaults.startChatError) };
    }
  };

  /**
   * "+": opens the chat. At the token limit it opens with no conversation and
   * the limit note; the first question asks /start again (sendMessage), so it
   * goes through as soon as the gateway allows.
   */
  const startChat = async (range: MaiaDateRange) => {
    if (!patientNumber) {
      set({ error: defaults.enterPatientNumber });
      return;
    }
    set({ dateRange: range, isStarting: true, error: null });
    const outcome = await requestStart(patientNumber, range);
    if ('error' in outcome) {
      set({ isStarting: false, error: outcome.error });
      return;
    }
    const opened = { view: 'chat' as const, messages: [], isStarting: false };
    set(
      'limit' in outcome
        ? {
            ...opened,
            conversation: null,
            chartStatus: null,
            tokenNotice: tokenNoticeFor(outcome.limit),
          }
        : {
            ...opened,
            conversation: outcome.conversation,
            chartStatus: outcome.chartStatus,
            tokenNotice: tokenNoticeFor(outcome.tokenStatus),
          },
    );
  };

  /**
   * true = answered, false = failed (the question stays in the transcript),
   * 'retry' = the question got no answer and was not kept (the agent was busy
   * with an earlier one, timed out, the chart was not ready, it failed, or the
   * agency is at its token limit): it
   * is removed, so the view can put it back in the composer.
   */
  const sendMessage = async (text: string): Promise<SendResult> => {
    const trimmed = text.trim();
    const { view, conversation: openConversation, responseDetail } = read();
    // One question at a time: the agent answers 423 to a second one.
    if (
      !trimmed ||
      view !== 'chat' ||
      !patientNumber ||
      pendingSendId != null
    ) {
      return false;
    }

    sendSeq += 1;
    const sendId = sendSeq;
    pendingSendId = sendId;
    const isCurrent = () => pendingSendId === sendId;

    const question: ChatMessage = {
      id: nextId(),
      role: 'user',
      content: trimmed,
    };
    set({
      messages: [...read().messages, question],
      isSending: true,
      isSlowReply: false,
      isAgentBusy: false,
      error: null,
    });
    const sentAt = Date.now();
    armSlowTimer(sendId);

    // Two outcomes: the agent's reply (an answer, or why there is none) here,
    // and every failure (HTTP error, timeout, dropped connection) in the catch.
    try {
      let conversation = openConversation;
      if (!conversation) {
        // opened at the token limit: start the conversation now
        const outcome = await requestStart(patientNumber, read().dateRange);
        if ('conversation' in outcome && !isCurrent()) {
          // Done meanwhile: end what nobody will use (best effort)
          const { recordId } = outcome.conversation;
          if (recordId != null) {
            agentGateway
              .endPatientChatConversation(recordId)
              .catch(() => undefined);
          }
        }
        if (!isCurrent()) {
          return false;
        }
        if (!('conversation' in outcome)) {
          set({
            messages: read().messages.filter((m) => m.id !== question.id),
            ...('limit' in outcome
              ? { tokenNotice: tokenNoticeFor(outcome.limit) }
              : { error: outcome.error }),
          });
          return 'retry';
        }
        conversation = outcome.conversation;
        // as on "+": the limit note gives way to the % used, or to nothing
        set({
          conversation,
          chartStatus: outcome.chartStatus,
          tokenNotice: tokenNoticeFor(outcome.tokenStatus),
        });
      }
      const response = unwrap(
        await agentGateway.sendPatientChatMessage({
          conversationId: conversation.externalId,
          message: trimmed,
          responseDetail,
        }),
      );
      // the agency's pool, whatever happened to the question
      updateTokenNotice(response.tokenStatus);
      if (!isCurrent()) {
        return false;
      }
      const body: Partial<PatientChatMessageBody> = response.body || {};
      if (body.cancelled) {
        // stopped by a cancel this widget did not wait for
        markCancelled(question.id);
        return false;
      }
      const notAnswered = notAnsweredText(body);
      if (notAnswered) {
        set({
          messages: read().messages.filter((m) => m.id !== question.id),
          error: notAnswered,
        });
        return 'retry';
      }
      set({
        chartStatus:
          read().chartStatus === 'generating' ? 'ready' : read().chartStatus,
        messages: [
          ...read().messages,
          {
            id: nextId(),
            role: 'assistant',
            content: body.reply || defaults.emptyReply,
            requestId: response.requestId,
          },
        ],
      });
      return true;
    } catch (err) {
      if (!isCurrent()) {
        return false;
      }
      if (isGatewayError(err) && err.status === 423) {
        // the agent is still on an earlier question: this one was not taken
        set({
          messages: read().messages.filter((m) => m.id !== question.id),
          isAgentBusy: true,
        });
        return 'retry';
      }
      const limit = await tokenLimitFromError(err);
      if (!isCurrent()) {
        return false;
      }
      if (limit) {
        // not asked: the question goes back in the box, the note says when to retry
        set({
          messages: read().messages.filter((m) => m.id !== question.id),
          tokenNotice: tokenNoticeFor(limit),
        });
        return 'retry';
      }
      set({
        error: sendErrorText(err, read().chartStatus, Date.now() - sentAt),
      });
      return false;
    } finally {
      if (isCurrent()) {
        releasePending();
      }
    }
  };

  /**
   * "Yes" to the cancel prompt: stops the question on the agent, then unlocks
   * the composer whatever the outcome, so the user is never left stuck.
   */
  const cancelPending = async () => {
    const { conversation, isCancelling, messages } = read();
    if (!conversation || isCancelling) {
      return;
    }
    const sendId = pendingSendId;
    const lastQuestion = [...messages].reverse().find((m) => m.role === 'user');
    set({ isCancelling: true });
    const outcome = await requestCancel(conversation.externalId);
    if (read().conversation !== conversation) {
      return; // Done was pressed meanwhile
    }
    set({ isCancelling: false, isAgentBusy: false });
    if (sendId != null && pendingSendId === sendId) {
      releasePending();
    }
    // marked only when the agent really stopped it (a pending question, or the
    // earlier one a 423 was about)
    if (outcome === 'stopped' && lastQuestion) {
      markCancelled(lastQuestion.id);
    }
    let error: string | null = null;
    if (outcome === 'failed') {
      error = defaults.cancelFailed;
    } else if (outcome === 'idle' && sendId != null) {
      error = defaults.cancelTooLate;
    }
    set({ error });
  };

  /** "No" to the cancel prompt: keep waiting, and ask again later. */
  const keepWaiting = () => {
    set({ isAgentBusy: false, isSlowReply: false });
    if (pendingSendId != null) {
      armSlowTimer(pendingSendId);
    }
  };

  const setFeedback = (messageId: string, feedback: AnswerFeedback | null) =>
    set({
      messages: read().messages.map((m) =>
        m.id === messageId ? { ...m, feedback: feedback ?? undefined } : m,
      ),
    });

  /**
   * Rates a live answer (null undoes it). Shown at once; put back and false
   * when the gateway refuses, so the view can say it was not saved.
   */
  const rateAnswer = async (
    messageId: string,
    feedback: AnswerFeedback | null,
  ): Promise<boolean> => {
    const { conversation } = read();
    const message = read().messages.find((m) => m.id === messageId);
    if (!conversation || message?.requestId == null) {
      return false;
    }
    const previous = message.feedback ?? null;
    setFeedback(messageId, feedback);
    try {
      unwrap(
        await agentGateway.ratePatientChatAnswer({
          requestId: message.requestId,
          rating: feedback?.rating ?? null,
          comment: feedback?.comment ?? null,
        }),
      );
      return true;
    } catch {
      if (read().conversation === conversation) {
        setFeedback(messageId, previous);
      }
      return false;
    }
  };

  /**
   * Done: back to history at once. The chat's cancel and /end finish in the
   * background; /end answering 409 means it already ended, which is fine.
   */
  const done = async () => {
    const { view, conversation, isAgentBusy } = read();
    const wasAnswering = pendingSendId != null || isAgentBusy;
    releasePending(); // a reply still in flight must not land in the next chat
    set({
      view: 'history',
      pastRow: null,
      pastQuestion: null,
      conversation: null,
      messages: [],
      chartStatus: null,
      isAgentBusy: false,
      isCancelling: false,
      error: null,
      tokenNotice: null,
    });
    if (view !== 'chat' || !conversation) {
      return;
    }
    if (wasAnswering) {
      // stop the agent's work on a question nobody will read (best effort)
      await requestCancel(conversation.externalId);
    }
    if (conversation.recordId == null) {
      return;
    }
    try {
      unwrap(
        await agentGateway.endPatientChatConversation(conversation.recordId),
      );
    } catch (err) {
      if (!(isGatewayError(err) && err.status === 409)) {
        set({ error: defaults.endChatError }); // 409: already ended, fine
      }
    }
  };

  return {
    key,
    get state() {
      return read();
    },
    setDateRange: (dateRange: MaiaDateRange) => set({ dateRange }),
    setResponseDetail: (responseDetail: PatientChatResponseDetail) =>
      set({ responseDetail }),
    setError: (error: string | null) => set({ error }),
    /** × on the token note: hidden until the next "+" or limit. */
    dismissTokenNotice: () => set({ tokenNotice: null }),
    openPast: (
      row: PatientConversationSummary,
      question: ConversationQuestion | null = null,
    ) => set({ view: 'past', pastRow: row, pastQuestion: question }),
    startChat,
    sendMessage,
    cancelPending,
    keepWaiting,
    rateAnswer,
    done,
    /** Back to a fresh widget (the tile was removed from the dashboard). */
    reset: () => {
      releasePending();
      set(initialState());
    },
  };
};

export type MaiaChat = ReturnType<typeof createMaiaChat>;

/**
 * The actions object per agency + patient, shared by every copy of the widget
 * on the page; its state is in `useMaiaChatStore`.
 */
const chats = new Map<string, MaiaChat>();

const getMaiaChat = (
  agency: string,
  patientNumber: string | null | undefined,
  agentGateway: AgentGateway,
): MaiaChat => {
  const key = `${agency}:${patientNumber ?? ''}`;
  let chat = chats.get(key);
  if (!chat) {
    chat = createMaiaChat(agentGateway, patientNumber, key);
    chats.set(key, chat);
  }
  return chat;
};

/** Tests only: forget every patient's state. */
export const clearMaiaChats = () => {
  chats.forEach((chat) => chat.reset());
  chats.clear();
  useMaiaChatStore.setState({ chats: {} });
};

/**
 * The patient's shared MAIA state; re-renders the caller on every change.
 * Read it from `chat.state`, change it only through the returned actions.
 */
export const useMaiaChat = (
  agency: string,
  patientNumber: string | null | undefined,
  agentGateway: AgentGateway,
): MaiaChat => {
  const chat = getMaiaChat(agency, patientNumber, agentGateway);
  // re-renders the caller whenever this patient's entry changes
  useMaiaChatStore((s) => s.chats[chat.key]);
  return chat;
};
